import React, { useContext, useState, useEffect } from "react";
import {
    WizardView,
    useWizardPage,
    useSyncedField,
    FinishPage,
} from "../../components/Wizard";
import { gql, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import Spinner from "../../components/Spinner";
import {
    DeviceTypePage,
    SignalsPage,
    validateSignalsReviewed,
    buildDeviceEffectiveConfiguration,
    fetchAvailableDeviceModels,
} from "../deviceProfile/AddDeviceProfileWizard";

const GET_DEVICE_PROFILES = gql`
    query GetDeviceProfiles($plant_id: Int!) {
        deviceProfiles(where: { plant_id: { equals: $plant_id } }) {
            id
            name
            description
            configuration
        }
    }
`;

// Source picker: either assign an existing Device Profile to this device
// (identical to the Profile tab's dropdown in DeviceEditSidebar - kept here
// too as a convenience shortcut), or configure this device's own values
// directly. Direct configuration never creates or touches a Device Profile -
// see AddDeviceConfigWizard's handleFinish and Devices.jsx's mount site.
function ConfigSourcePage({ device }) {
    const starekContext = useContext(StarekContext);
    const deviceModel = device?.device?.model;
    const [showIncompatible, setShowIncompatible] = useState(false);
    const [configSource, setConfigSource] = useSyncedField(
        "config_source",
        "existing"
    );
    const [selectedProfileId, setSelectedProfileId] = useSyncedField(
        "selected_profile_id",
        ""
    );
    const [, setSelectedProfileName] = useSyncedField(
        "selected_profile_name",
        null
    );

    const { data, loading, error } = useQuery(GET_DEVICE_PROFILES, {
        variables: { plant_id: starekContext?.lastUsedPlant?.id },
        skip: !starekContext?.lastUsedPlant?.id,
        client: starekContext.engineClient,
    });

    const deviceProfiles = data?.deviceProfiles || [];

    // A profile's signal names/pins only make sense for the exact model it
    // was authored against (see backend's assignDeviceProfile, which
    // enforces this too) - a profile with no model set yet (still
    // unconfigured) isn't "incompatible", it just has nothing to conflict
    // with, so it's always shown.
    const isProfileCompatible = (p) => {
        const profileModel = p.configuration?.model?.code;
        return !profileModel || !deviceModel || profileModel === deviceModel;
    };
    const visibleProfiles = showIncompatible
        ? deviceProfiles
        : deviceProfiles.filter(isProfileCompatible);

    useEffect(() => {
        if (configSource === "existing" && selectedProfileId) {
            const profile = deviceProfiles.find(
                (p) => String(p.id) === String(selectedProfileId)
            );
            if (profile) setSelectedProfileName(profile.name);
        }
    }, [configSource, selectedProfileId, deviceProfiles, setSelectedProfileName]);

    if (loading) return <Spinner />;
    if (error) return <div className="text-danger">Error loading profiles</div>;

    return (
        <div>
            <h3 className="mb-3">Configuration Source</h3>
            <p className="text-muted mb-4">
                Choose how you want to configure this device.
            </p>

            <div className="mb-4">
                <div className="form-check mb-2">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="configSource"
                        id="sourceExisting"
                        value="existing"
                        checked={configSource === "existing"}
                        onChange={(e) => setConfigSource(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="sourceExisting">
                        Apply an existing Device Profile
                    </label>
                </div>
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="configSource"
                        id="sourceNew"
                        value="new"
                        checked={configSource === "new"}
                        onChange={(e) => setConfigSource(e.target.value)}
                    />
                    <label className="form-check-label" htmlFor="sourceNew">
                        Configure this device directly
                    </label>
                </div>
            </div>

            {configSource === "existing" && (
                <div className="mb-3">
                    <label className="form-label">Select Device Profile</label>
                    <select
                        className="form-select"
                        value={selectedProfileId}
                        onChange={(e) => setSelectedProfileId(e.target.value)}
                    >
                        <option value="">-- Select a Profile --</option>
                        {visibleProfiles.map((p) => {
                            const compatible = isProfileCompatible(p);
                            return (
                                <option
                                    key={p.id}
                                    value={p.id}
                                    disabled={!compatible}
                                >
                                    {p.name}
                                    {!compatible &&
                                        p.configuration?.model?.name
                                        ? ` (for ${p.configuration.model.name})`
                                        : ""}
                                </option>
                            );
                        })}
                    </select>
                    {deviceModel && (
                        <div className="form-check mt-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showIncompatibleProfiles"
                                checked={showIncompatible}
                                onChange={(e) =>
                                    setShowIncompatible(e.target.checked)
                                }
                            />
                            <label
                                className="form-check-label small text-muted"
                                htmlFor="showIncompatibleProfiles"
                            >
                                Show incompatible profiles
                            </label>
                        </div>
                    )}
                </div>
            )}

            {configSource === "new" && (
                <p className="text-muted small">
                    The next steps let you set this device's own signal
                    roles, names, and colors directly - independent of any
                    Device Profile. You can optionally save the result as a
                    new Device Profile at the end, for reuse on other
                    devices.
                </p>
            )}
        </div>
    );
}

// Its own dedicated page (not folded into Review) so the option is visible
// on first pass, not just something a user stumbles on while reviewing -
// only shown for the direct-configure path, since assigning an existing
// profile already IS a reusable template with nothing new to save.
function SaveAsProfilePage() {
    const [saveAsProfile, setSaveAsProfile] = useSyncedField(
        "save_as_profile",
        false
    );
    const [profileName, setProfileName] = useSyncedField(
        "save_as_profile_name",
        ""
    );
    const [profileDescription, setProfileDescription] = useSyncedField(
        "save_as_profile_description",
        ""
    );

    return (
        <div>
            <h3 className="mb-3">Save as Device Profile</h3>
            <p className="text-muted mb-4">
                You've just configured this device directly. Optionally save
                the result as a reusable Device Profile, so the same setup
                can be applied to other devices later.
            </p>

            <div className="form-check mb-2">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="saveAsProfile"
                    checked={saveAsProfile}
                    onChange={(e) => setSaveAsProfile(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="saveAsProfile">
                    Also save this configuration as a new Device Profile
                </label>
            </div>
            {saveAsProfile && (
                <div className="ps-4">
                    <div className="mb-2">
                        <label className="form-label">
                            Profile Name <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={profileName}
                            onChange={(e) => setProfileName(e.target.value)}
                            placeholder="e.g. Line 3 Sewing Machine"
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            rows="2"
                            value={profileDescription}
                            onChange={(e) =>
                                setProfileDescription(e.target.value)
                            }
                            placeholder="Optional description..."
                        ></textarea>
                    </div>
                </div>
            )}
        </div>
    );
}

function ReviewPage() {
    const { emittedManifest } = useWizardPage();

    const isDirect = emittedManifest?.config_source === "new";
    const selectedProfile = emittedManifest?.selected_profile_name;

    return (
        <div>
            <h3 className="mb-3">Review & Finish</h3>

            <div className="card mb-3">
                <div className="card-body">
                    <h5 className="card-title">Configuration Summary</h5>
                    <dl className="row mb-0">
                        <dt className="col-sm-4">Source</dt>
                        <dd className="col-sm-8">
                            {isDirect
                                ? "Configured directly on this device"
                                : "Existing Profile"}
                        </dd>

                        {!isDirect && (
                            <>
                                <dt className="col-sm-4">Profile</dt>
                                <dd className="col-sm-8">{selectedProfile || "N/A"}</dd>
                            </>
                        )}
                    </dl>
                </div>
            </div>

            <FinishPage />
        </div>
    );
}

export default function AddDeviceConfigWizard(props) {
    const starekContext = useContext(StarekContext);
    const deviceFlags = starekContext?.types?.device?.flags;

    const [seedManifest, setSeedManifest] = useState(null);
    const [loadingManifest, setLoadingManifest] = useState(true);

    // The device's effective (current, merged) configuration is fetched once
    // up front and passed in as the wizard's emittedManifest (useSyncedField/
    // useRuntimeField only ever read from emittedManifest/runtimeVariables,
    // never from inputManifest - same mechanism DeviceProfiles.jsx already
    // uses to seed the wizard when editing an existing profile), so
    // DeviceTypePage pre-selects this device's own model (locked, can't be
    // changed) and SignalsPage pre-fills every signal with what the device
    // actually has right now - just sourced from ShadowDevice.parameters/
    // Device.overrides instead of DeviceProfile.configuration.
    useEffect(() => {
        let cancelled = false;
        async function loadEffectiveConfig() {
            if (!props.device) {
                setLoadingManifest(false);
                return;
            }
            try {
                const models = await fetchAvailableDeviceModels(starekContext);
                if (cancelled) return;
                const effectiveConfig = buildDeviceEffectiveConfiguration(
                    props.device,
                    models,
                    deviceFlags
                );
                setSeedManifest(effectiveConfig || {});
            } catch (e) {
                if (!cancelled) {
                    starekContext.showError?.(
                        `Failed to load this device's current configuration: ${e.message}`
                    );
                    setSeedManifest({});
                }
            } finally {
                if (!cancelled) setLoadingManifest(false);
            }
        }
        loadEffectiveConfig();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.device?.id]);

    const handleFinish = async (emittedManifest) => {
        if (props.onFinish) {
            await props.onFinish(emittedManifest);
        }
    };

    if (loadingManifest) {
        return (
            <div className="text-center p-5">
                <Spinner />
            </div>
        );
    }

    return (
        <WizardView
            onClose={props.onClose}
            onFinish={handleFinish}
            title="Configure Device"
            width="1100px"
            height="600px"
            showFinishPage={false}
            emittedManifest={seedManifest}
        >
            <WizardView.Page
                title="Source"
                icon="cog"
                onNextRequested={(e) => {
                    const { config_source, selected_profile_id } =
                        e.detail.emittedManifest || {};
                    if (config_source === "existing" && !selected_profile_id) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <ConfigSourcePage device={props.device} />
            </WizardView.Page>

            <WizardView.Page
                title="Device Type"
                icon="microchip"
                condition={(vars) => vars.config_source === "new"}
            >
                <DeviceTypePage locked />
            </WizardView.Page>

            <WizardView.Page
                title="Signals"
                icon="arrow-down"
                condition={(vars) => vars.config_source === "new"}
                onNextRequested={validateSignalsReviewed}
            >
                <SignalsPage />
            </WizardView.Page>

            <WizardView.Page
                title="Save as Profile"
                icon="save"
                condition={(vars) => vars.config_source === "new"}
                onNextRequested={(e) => {
                    const { save_as_profile, save_as_profile_name } =
                        e.detail.emittedManifest || {};
                    if (save_as_profile && !save_as_profile_name) {
                        e.detail.prevented = true;
                    }
                }}
            >
                <SaveAsProfilePage />
            </WizardView.Page>

            <WizardView.Page title="Review" icon="check-circle">
                <ReviewPage />
            </WizardView.Page>
        </WizardView>
    );
}
